import { motion } from 'framer-motion';
import { useSEO } from '../../hooks/useSEO';
import { generateAboutPageSchema, generateBreadcrumbSchema } from '../../utils/seo';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Breadcrumb from '../../components/base/Breadcrumb';
import { Link } from 'react-router-dom';

export default function SobrePage() {
  // SEO
  useSEO({
    title: 'Sobre Mateus Oliveira - Corretor de Imóveis em Aracaju e Barra dos Coqueiros',
    description: 'Conheça Mateus Oliveira, corretor especializado em imóveis de médio e alto padrão em Aracaju e Barra dos Coqueiros, Sergipe. Atendimento personalizado e consultivo. WhatsApp (79) 99988-1002',
    keywords: 'mateus oliveira corretor, corretor aracaju, imobiliária sergipe, corretor barra dos coqueiros, sobre mateus oliveira',
    canonical: window.location.origin + '/sobre',
    schema: [
      generateAboutPageSchema(),
      generateBreadcrumbSchema([{ name: 'Sobre', url: '/sobre' }]),
    ],
  });

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={[{ name: 'Sobre', url: '/sobre' }]} />

          <div className="pt-24 pb-16">
            <div className="max-w-7xl mx-auto px-6">
              {/* Hero */}
              <div className="text-center mb-16">
                <h1 className="text-6xl font-bold text-gray-900 mb-6 font-serif">
                  Sobre a Mateus Oliveira Imóveis
                </h1>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Mais de uma década transformando sonhos em realidade no mercado imobiliário de Aracaju e Barra dos Coqueiros
                </p>
              </div>

              {/* História */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
                <div>
                  <div className="inline-block bg-orange-100 text-orange-900 px-4 py-2 rounded-lg mb-6">
                    Nossa História
                  </div>
                  <h2 className="text-4xl font-bold text-gray-900 mb-6 font-serif">
                    Experiência e Compromisso com Sergipe
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Fundada em 2014, a Mateus Oliveira Imóveis nasceu do sonho de oferecer 
                      um atendimento diferenciado no mercado imobiliário de Sergipe. Com atuação 
                      em Aracaju e Barra dos Coqueiros, rapidamente nos tornamos referência em qualidade e confiança.
                    </p>
                    <p>
                      Ao longo dos anos, ajudamos centenas de famílias a realizarem o sonho 
                      da casa própria, sempre com transparência, profissionalismo e dedicação. 
                      Nossa equipe especializada conhece profundamente o mercado local e está 
                      preparada para oferecer as melhores soluções.
                    </p>
                    <p>
                      Trabalhamos com imóveis residenciais e comerciais em Aracaju e Barra dos Coqueiros, 
                      oferecendo serviços completos de compra, venda e locação. Cada cliente é único e merece 
                      um atendimento personalizado que atenda suas necessidades específicas.
                    </p>
                  </div>
                </div>

                <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ height: '600px' }}>
                  <img
                    src="https://public.readdy.ai/ai/img_res/f6ea615584df914e6d53faae016e4259.jpg"
                    alt="Escritório Mateus Oliveira Imóveis"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Valores */}
              <div className="mb-24">
                <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center font-serif">
                  Nossos Valores
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[
                    {
                      icon: 'ri-shield-check-line',
                      title: 'Confiança',
                      description: 'Transparência em todas as negociações e compromisso com a verdade'
                    },
                    {
                      icon: 'ri-customer-service-line',
                      title: 'Atendimento',
                      description: 'Suporte personalizado e dedicação total aos nossos clientes'
                    },
                    {
                      icon: 'ri-award-line',
                      title: 'Excelência',
                      description: 'Busca constante pela qualidade e satisfação em cada detalhe'
                    }
                  ].map((valor, index) => (
                    <div key={index} className="bg-gray-50 rounded-2xl p-8 text-center">
                      <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className={`${valor.icon} text-3xl`}></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{valor.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{valor.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Números */}
              <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-3xl p-16 mb-24">
                <h2 className="text-4xl font-bold text-white mb-12 text-center font-serif">
                  Resultados que Falam por Si
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {[
                    { number: '10+', label: 'Anos de Experiência' },
                    { number: '500+', label: 'Imóveis Vendidos' },
                    { number: '1000+', label: 'Clientes Satisfeitos' },
                    { number: '98%', label: 'Satisfação' }
                  ].map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="text-5xl font-bold text-white mb-2">{stat.number}</div>
                      <div className="text-white/80">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="text-center">
                <h2 className="text-4xl font-bold text-gray-900 mb-6 font-serif">
                  Pronto para Encontrar Seu Imóvel?
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  Entre em contato conosco e descubra as melhores oportunidades em Aracaju e Barra dos Coqueiros
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/imoveis"
                    className="bg-orange-600 text-white px-8 py-4 rounded-lg hover:bg-orange-700 transition-colors font-medium whitespace-nowrap"
                  >
                    Ver Imóveis
                  </Link>
                  <Link
                    to="/contato"
                    className="bg-gray-100 text-gray-900 px-8 py-4 rounded-lg hover:bg-gray-200 transition-colors font-medium whitespace-nowrap"
                  >
                    Falar Conosco
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <WhatsAppButton />
      <Footer />
    </>
  );
}
